import { type FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { ErrorBox } from '../components/ui/Card'
import { inputClass } from '../components/ui/FormField'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })
      // Always show success — backend never reveals whether email exists
      setSubmitted(true)
    } catch {
      setError('Network error. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-md text-center">
          <div className="text-[3rem] mb-4">📧</div>
          <h1 className="m-0 mb-3 text-[1.3rem] font-bold text-text-heading">Check your email</h1>
          <p className="text-text-2 leading-[1.6] mb-6 text-[0.95rem]">
            If <strong>{email}</strong> is registered, we've sent a password reset link.
            Check your inbox — it expires in 60 minutes.
          </p>
          <Link to="/login" className="text-accent font-semibold text-[0.9rem] no-underline hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-md">
        <div className="text-center font-bold text-[1.35rem] text-accent mb-1">Vet VPMS</div>
        <div className="text-center text-text-3 text-[0.9rem] mb-8">Enter your email to reset your password</div>

        {error && <ErrorBox>{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <label className="block text-sm font-semibold text-text-heading mb-[0.35rem]" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            className={`${inputClass(false)} mb-[1.1rem]`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            disabled={submitting}
          />
          <Button type="submit" disabled={submitting} className="w-full mt-2">
            {submitting ? 'Sending…' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="text-center mt-5 text-sm text-text-3">
          <Link to="/login" className="text-accent font-semibold no-underline hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
